import React, {useCallback, useEffect, useState} from 'react'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import PropTypes from 'prop-types'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useBrand} from '../../contexts/brand-context'
import {useAddToCartSource} from '../../contexts/add-to-cart-source-context'
import {INTERACTION_LOCATION, sendClickEvent} from '../../utils/gtm-utils'
import {persistPendingWishlist} from '../../utils/wishlist-utils'

const getWishlistInteractionLocation = (contextInteractionLocation, wishlistClickPage) => {
    if (contextInteractionLocation) return contextInteractionLocation
    if (wishlistClickPage === 'pdp') return INTERACTION_LOCATION.PDP
    if (wishlistClickPage === 'plp') return INTERACTION_LOCATION.PLP
    return null
}

/**
 * Higher-order component that modifies the given component's `onClick` to show the login form if
 * the user is not logged in.
 * @param {React.Component} component Component to wrap.
 * @param {(location: Location, locale: string) => boolean} [options.isLoginPage] Function that
 * determines whether the current page is the "login" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/login".
 * @param {(location: Location, locale: string) => boolean} [options.isRegistrationPage] Function that
 * determines whether the current page is the "registration" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/registration".
 * @param {boolean} [options.isWishlistAction] Flag to indicate this is a wishlist action, which will
 * show the wishlist message in the auth modal.
 * @returns {React.Component} wrapped component
 */
const withRegistration = (
    Component,
    {
        isLoginPage = (loc) => loc.pathname.endsWith('/login'),
        isRegistrationPage = (loc) => loc.pathname.endsWith('/registration'),
        isWishlistAction = false
    } = {}
) => {
    const WrappedComponent = ({
        onClick = noop,
        wishlistProductId: wishlistProductIdProp,
        productId: productIdProp,
        wishlistClickPage,
        ...passThroughProps
    }) => {
        const {data: customer} = useCurrentCustomer()
        const authModal = useAuthModal()
        const {showCustomToast} = useCustomToast()
        const location = useLocation()
        const {formatMessage, locale} = useIntl()
        const {interactionLocation: contextInteractionLocation} = useAddToCartSource()
        const [pendingAction, setPendingAction] = useState(false)
        // The modal (and its ~25 hooks) mounts only once a click actually needs it.
        const [authRequested, setAuthRequested] = useState(false)
        const wishlistProductId =
            wishlistProductIdProp ||
            passThroughProps?.['data-wishlist-product-id'] ||
            productIdProp
        const {isGarage} = useBrand()

        const runPendingAction = useCallback(() => {
            if (!pendingAction) return
            if (isWishlistAction) {
                // For wishlist actions, wait until customerId is available so add-to-wishlist can succeed.
                return
            }
            setPendingAction(false)
            onClick()
        }, [pendingAction, onClick, isWishlistAction])

        useEffect(() => {
            if (!pendingAction || !customer?.isRegistered) return

            if (isWishlistAction) {
                // Wishlist replay is centralized by PendingWishlistReplay to avoid
                // duplicate triggers across multiple withRegistration instances.
                setPendingAction(false)
                return
            }

            runPendingAction()
        }, [
            pendingAction,
            customer?.isRegistered,
            isWishlistAction,
            runPendingAction
        ])

        const handleClick = (e) => {
            e.preventDefault()

            // Fire custom_click event on the wishlist-icon click.
            if (isWishlistAction && wishlistClickPage) {
                const authState = customer?.isRegistered ? 'logged_in' : 'guest'
                sendClickEvent(`wishlist-icon - ${wishlistClickPage} - ${authState}`)
            }

            if (!customer?.isRegistered) {
                if (isWishlistAction && wishlistProductId && typeof window !== 'undefined') {
                    persistPendingWishlist({
                        productId: wishlistProductId,
                        interactionLocation: getWishlistInteractionLocation(
                            contextInteractionLocation,
                            wishlistClickPage
                        )
                    })
                }
                setPendingAction(true)
                if (isLoginPage(location, locale)) {
                    const message = isGarage
                        ? formatMessage({
                              id: 'with_registration.info.please_sign_in.garage',
                              defaultMessage: 'Please sign in to continue!'
                          })
                        : formatMessage({
                              id: 'with_registration.info.please_sign_in.dynamite',
                              defaultMessage: 'Please sign in to continue!'
                          })

                    showCustomToast({message})
                } else if (isRegistrationPage(location, locale)) {
                    const message = isGarage
                        ? formatMessage({
                              id: 'with_registration.info.already_on_registration.garage',
                              defaultMessage: 'You are already on the registration page.'
                          })
                        : formatMessage({
                              id: 'with_registration.info.already_on_registration.dynamite',
                              defaultMessage: 'You are already on the registration page.'
                          })

                    showCustomToast({message})
                } else {
                    // Set the wishlist flag if this is a wishlist action
                    if (isWishlistAction) {
                        authModal.setIsWishlistClick(true)
                    }
                    setAuthRequested(true)
                    authModal.onOpen()
                }
            } else {
                onClick()
            }
        }

        return (
            <React.Fragment>
                <Component {...passThroughProps} onClick={handleClick} />
                {authRequested && (
                    <AuthModal
                        {...authModal}
                        onLoginSuccess={runPendingAction}
                        onRegistrationSuccess={runPendingAction}
                    />
                )}
            </React.Fragment>
        )
    }

    WrappedComponent.propTypes = {
        onClick: PropTypes.func,
        wishlistClickPage: PropTypes.oneOf(['pdp', 'plp'])
    }

    return WrappedComponent
}

export default withRegistration
